import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { UTILITY_USER_ROLE_ADMIN, hasRoleAdmin } from 'common/utility/userRoles';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS_PARTNER } from 'client/constants/screenIds';

import UtilityPartnerActions from 'client/actions/UtilityPartner';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles, { getUserLandingPage } from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

import PartnerStatusUpdateCard from 'client/components/SrUtilityProfit/PartnerStatusUpdateCard';
import TenantLiabilityBannerCard from 'client/components/SrUtilityProfit/TenantLiabilityBannerCard';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS_PARTNER)
@withScrollToTop
@branch(({ match }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityUser: ['utilityUser'],
}))
class UtilityPartnerSettingsPartnerPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Settings',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const utilityPartner = inject.state.get(['utilityPartners', 'bySlug', partnerSlug]);

    const promises = [];

    if (!utilityPartner) {
      promises.push(
        UtilityPartnerActions.getBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    utilityUser: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
    };
    this.redirectTimeoutId = null;
  }

  componentWillUnmount() {
    if (this.redirectTimeoutId) {
      clearTimeout(this.redirectTimeoutId);
    }
  }

  _handleSuccess = () => {
    const { utilityPartner, history } = this.props;

    this.setState({ shouldShowSavedModal: true });

    if (this.redirectTimeoutId) {
      clearTimeout(this.redirectTimeoutId);
    }

    this.redirectTimeoutId = setTimeout(() => {
      this.setState({ shouldShowSavedModal: false });
      history.push(`/utility-profit/${utilityPartner.slug}/settings`);
    }, 1500);
  };

  render() {
    const { utilityPartner, utilityUser, isTransitioning, dispatch } = this.props;

    const { shouldShowSavedModal } = this.state;

    if (isTransitioning) {
      return null;
    }

    if (!utilityPartner) {
      const userLandingPage = getUserLandingPage({
        userRoles: utilityUser?.roles || [],
      });
      return <Redirect to={userLandingPage} />;
    }

    const isAdmin = hasRoleAdmin(utilityUser?.roles || []);

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}/settings`}
        navbarTitle={utilityPartner.name}
        navbarImage={utilityPartner.logoUrl}
        redirectToLastLocation={false}
        pageHeader="Partner Settings"
      >
        <SrBox marginBottom="100px">
          <SrBox marginBottom="24px">
            <PartnerStatusUpdateCard
              utilityPartner={utilityPartner}
              isAdmin={isAdmin}
              dispatch={dispatch}
              onSuccess={this._handleSuccess}
            />
          </SrBox>
          {isAdmin && (
            <TenantLiabilityBannerCard utilityPartner={utilityPartner} dispatch={dispatch} />
          )}
        </SrBox>
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsPartnerPage.displayName = 'UtilityPartnerSettingsPartnerPage';

export default UtilityPartnerSettingsPartnerPage;
